import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ShieldCheck, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { serviceGroups } from "../data/services";
import { legal, has, OUTCOME_DISCLAIMER } from "../data/legal";
import { submitConsultation, buildConsent } from "../config/maraApi";

const WINDOWS = [
  { value: "", label: "No preference" },
  { value: "morning", label: "Morning" },
  { value: "afternoon", label: "Afternoon" },
  { value: "evening", label: "Evening" },
];

export default function BookConsultation() {
  const [params] = useSearchParams();
  const preselect = params.get("service") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    visa: preselect,
    date: "",
    window: "",
    timezone: "",
    message: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !privacy) return;
    setStatus("submitting");
    setError("");
    const groupOf = serviceGroups.find((g) =>
      g.services.some((s) => s.slug === form.visa)
    );
    try {
      await submitConsultation({
        name: form.name,
        email: form.email,
        phone: form.phone,
        visaGroup: groupOf?.slug || "",
        visa: form.visa,
        preferredTime: [form.date, form.window].filter(Boolean).join(" "),
        timezone: form.timezone,
        message: form.message,
        consent: buildConsent({ privacyAccepted: privacy }),
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err.message || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <>
        <SEO title="Book a consultation" path="/book-consultation/" />
        <section className="container-page max-w-2xl py-20 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-acblue text-white">
            <Check className="h-7 w-7" />
          </div>
          <h1 className="mt-6 text-3xl font-bold text-acblue">
            Thanks, {form.name.split(" ")[0]}.
          </h1>
          <p className="mx-auto mt-3 max-w-md text-acBlack/70">
            Thanks, we have your request. We will be in touch very soon to confirm a time.
            This is a support and preparation conversation. The regulated advice is
            provided by a registered migration agent we match you with.
          </p>
          <Link to="/" className="btn-ghost mt-8">
            Back home
          </Link>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Book a consultation"
        description="Book a support and preparation conversation. We help you get application-ready and match you with a registered migration agent for the regulated advice and lodgement."
        path="/book-consultation/"
      />
      <Breadcrumbs
        items={[{ label: "Home", to: "/" }, { label: "Book a consultation" }]}
      />

      <section className="container-page py-12">
        <p className="eyebrow">Get started</p>
        <h1 className="mt-3 text-4xl font-bold text-acblue sm:text-5xl">
          Book a consultation
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-acBlack/70">
          Talk through your situation with us. We will help you get
          application-ready, explain the next steps, and match you with a
          registered migration agent who gives the regulated advice and lodges
          your application. No outcome is guaranteed; visa decisions are made by
          the Department of Home Affairs.
        </p>
      </section>

      <section className="bg-acBg py-16">
        <div className="container-page grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <form className="card space-y-4" onSubmit={submit}>
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  required
                />
                <input
                  type="email"
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  required
                />
                <input
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                  placeholder="Phone (include country code)"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  required
                />
                <select
                  className="w-full rounded-xl border border-acblue/15 bg-white px-4 py-3 text-sm outline-none focus:border-acAccent"
                  value={form.visa}
                  onChange={(e) => set("visa", e.target.value)}
                >
                  <option value="">Visa of interest (optional)</option>
                  {serviceGroups.map((g) => (
                    <optgroup key={g.slug} label={g.title}>
                      {g.services.map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.title}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <input
                  type="date"
                  className="w-full rounded-xl border border-acblue/15 bg-white px-4 py-3 text-sm text-acBlack/70 outline-none focus:border-acAccent"
                  value={form.date}
                  onChange={(e) => set("date", e.target.value)}
                  aria-label="Preferred date"
                />
                <select
                  className="w-full rounded-xl border border-acblue/15 bg-white px-4 py-3 text-sm outline-none focus:border-acAccent"
                  value={form.window}
                  onChange={(e) => set("window", e.target.value)}
                  aria-label="Preferred time of day"
                >
                  {WINDOWS.map((w) => (
                    <option key={w.value} value={w.value}>
                      {w.label}
                    </option>
                  ))}
                </select>
                <input
                  className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent sm:col-span-2"
                  placeholder="Your timezone (optional, e.g. AEST, GMT+5:30)"
                  value={form.timezone}
                  onChange={(e) => set("timezone", e.target.value)}
                />
              </div>
              <textarea
                rows={4}
                className="w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                placeholder="Tell us a little about your situation"
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
              <label className="flex items-start gap-2 text-xs text-acBlack/70">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                  required
                />
                <span>
                  I have read the{" "}
                  <Link to="/privacy-policy/" className="text-acAccent underline">
                    Privacy Policy
                  </Link>{" "}
                  and consent to Migration & More collecting and using my personal
                  information to respond to my enquiry.
                </span>
              </label>
              {status === "error" && (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              )}
              <button
                type="submit"
                className="btn-primary w-full sm:w-auto"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Sending..." : "Request my consultation"}
              </button>
              <p className="text-[11px] text-acBlack/50">{OUTCOME_DISCLAIMER}</p>
            </form>
          </div>

          <aside className="space-y-4">
            <div className="card">
              <h2 className="text-lg font-semibold text-acblue">What to expect</h2>
              <ul className="mt-4 space-y-3 text-sm text-acBlack/75">
                <li className="flex gap-2">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-acAccent" />
                  A clear picture of what your application will need.
                </li>
                <li className="flex gap-2">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-acAccent" />
                  Matching with a registered migration agent for the regulated advice.
                </li>
                <li className="flex gap-2">
                  <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-acAccent" />
                  Fees in writing, no pressure, and no false promises.
                </li>
              </ul>
            </div>
            <div className="flex gap-3 rounded-2xl bg-white p-5 ring-1 ring-acblue/5">
              <ShieldCheck className="h-5 w-5 shrink-0 text-acAccent" />
              <p className="text-xs text-acBlack/60">
                We only work with registered agents, bound by the Code of
                Conduct and professionally insured, and we keep your matter
                moving.
              </p>
            </div>
            {(has(legal.phone) || has(legal.email)) && (
              <div className="card text-sm text-acBlack/75">
                <p className="font-semibold text-acblue">Prefer to reach us directly?</p>
                {has(legal.phone) && <p className="mt-2">{legal.phone}</p>}
                {has(legal.email) && <p>{legal.email}</p>}
              </div>
            )}
          </aside>
        </div>
      </section>
    </>
  );
}
